import { getToken, clearToken } from '../utils/authToken';
/**
 * ImageGallery - Galería de imágenes generadas
 * Permite ver, seleccionar, borrar individual y masivamente las imágenes guardadas
 * Ahora con persistencia en MongoDB
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { 
  Image, Trash2, X, Check, Square, CheckSquare, Download, 
  Maximize2, Loader2, AlertCircle, ImageOff, ZoomIn, ZoomOut,
  Copy, Save, ExternalLink, Archive
} from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';

import { BACKEND_URL } from '../config/api';
import { saveBlobWithPicker } from '../utils/downloadBlob';
const API = `${BACKEND_URL}/api`;

// Fullscreen viewer for gallery images
const GalleryFullscreenViewer = ({ image, onClose, onDelete, t }) => {
  const [zoom, setZoom] = useState(1);
  const [copied, setCopied] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === '+' || e.key === '=') setZoom(z => Math.min(z + 0.25, 3));
      if (e.key === '-') setZoom(z => Math.max(z - 0.25, 0.5));
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Construir imgSrc defensivamente:
  //  - Si image.base64 viene vacio/null (Object Store fallo o doc sin inline)
  //    se cae a image.url / image.image_url / image.preview_url (link http al backend).
  //  - Si nada de eso existe, queda null y mostramos UI de error.
  const buildSrc = (img) => {
    if (!img) return null;
    if (img.base64 && typeof img.base64 === 'string' && img.base64.length > 16) {
      return img.base64.startsWith('data:')
        ? img.base64
        : `data:image/png;base64,${img.base64}`;
    }
    // Fallbacks comunes en mobile cuando el backend no devolvio base64.
    const fallback = img.url || img.image_url || img.preview_url || img.src;
    if (fallback) {
      return fallback.startsWith('/api') ? `${API}${fallback}` : fallback;
    }
    return null;
  };
  const imgSrc = buildSrc(image);

  // Pre-cargar la imagen en memoria antes de montar el <img>
  // para evitar flash con dimensiones naturales.
  useEffect(() => {
    setImgLoaded(false);
    setImgError(false);
    if (!imgSrc) {
      // No hay fuente valida — desbloqueamos la UI con estado de error.
      setImgError(true);
      setImgLoaded(true);
      return;
    }
    const pre = new window.Image();
    pre.onload = () => setImgLoaded(true);
    pre.onerror = () => {
      // Mobile fix: ANTES quedaba colgado en opacity-0 = pantalla oscura.
      // Ahora desbloqueamos siempre y mostramos placeholder de error.
      setImgError(true);
      setImgLoaded(true);
    };
    pre.src = imgSrc;
    return () => {
      pre.onload = null;
      pre.onerror = null;
    };
  }, [imgSrc]);

  const handleDownload = async () => {
    try {
      // Obtener el base64 sin el prefijo data:image/...
      let base64Data = image.base64 || '';
      if (base64Data.startsWith('data:')) {
        base64Data = base64Data.split(',')[1];
      }

      // Convertir base64 a bytes binarios
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'image/png' });
      const filename = `damyxia_${image.id || Date.now()}.png`;

      // Estrategia universal: abrir en nueva pestana. El usuario usa el
      // menu del navegador → "Guardar imagen como..." para elegir carpeta.
      const result = await saveBlobWithPicker(blob, filename, 'image/png');
      if (result === 'fallback') {
        toast.success(t.imageDownloaded);
      } else {
        toast.success(
          t.imageOpenedInNewTab ||
            'Imagen abierta en nueva pestaña. Click derecho → "Guardar imagen como…" para elegir carpeta.',
          { duration: 7000 }
        );
      }
    } catch (error) {
      console.error('Error downloading image:', error);
      toast.error(t.errorDownloading);
    }
  };

  const handleCopy = async () => {
    const imgSrc = image.base64?.startsWith('data:')
      ? image.base64
      : `data:image/png;base64,${image.base64}`;

    // Estrategia en cascada — el iframe cross-origin de Emergent + Safari +
    // permissions-policy hacen que `clipboard.write` con ClipboardItem falle
    // muchas veces. Bajamos al fallback sin ensuciar al usuario con un
    // "Error al copiar" si alguna de las alternativas funciona.
    let copied = false;

    // 1) Copiar la imagen como blob — mejor experiencia (pegás imagen real).
    try {
      if (navigator.clipboard && typeof window.ClipboardItem === 'function' && navigator.clipboard.write) {
        const response = await fetch(imgSrc);
        const blob = await response.blob();
        await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
        copied = true;
        toast.success(t.imageCopied);
      }
    } catch (_e1) { /* sigue con fallback */ }

    // 2) Copiar el data URL (texto) — sirve en cualquier navegador y se pega
    //    en la barra de direcciones de otra pestana para abrir la imagen.
    if (!copied) {
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(imgSrc);
          copied = true;
          toast.success(t.urlCopied);
        }
      } catch (_e2) { /* sigue con fallback */ }
    }

    // 3) Fallback legacy con execCommand para navegadores sin clipboard API
    //    (Safari iOS viejo, contextos no seguros).
    if (!copied) {
      try {
        const ta = document.createElement('textarea');
        ta.value = imgSrc;
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        ta.setAttribute('readonly', '');
        document.body.appendChild(ta);
        ta.select();
        const ok = document.execCommand && document.execCommand('copy');
        document.body.removeChild(ta);
        if (ok) {
          copied = true;
          toast.success(t.urlCopied);
        }
      } catch (_e3) { /* nada mas que intentar */ }
    }

    if (!copied) {
      toast.error(t.copyError);
      return;
    }

    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return createPortal(
    <div className="fixed inset-0 z-[10000] bg-black/95 backdrop-blur-sm flex flex-col animate-in fade-in duration-200">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-zinc-900/80 border-b border-zinc-800">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-r from-purple-600 to-pink-600 rounded-lg">
            <Image className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-white text-sm">{t.fullscreenView}</h3>
            <p className="text-xs text-zinc-400 truncate max-w-[300px] md:max-w-[500px]">{image.prompt}</p>
          </div>
        </div>
        <button onClick={onClose} className="p-2 hover:bg-zinc-800 rounded-lg transition-colors group">
          <X className="w-6 h-6 text-zinc-400 group-hover:text-white" />
        </button>
      </div>

      {/* Image */}
      <div className="flex-1 flex items-center justify-center overflow-auto p-4 relative">
        {!imgLoaded && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <Loader2 className="w-10 h-10 animate-spin text-indigo-400" />
          </div>
        )}
        {imgError && imgLoaded && (
          <div className="flex flex-col items-center justify-center text-zinc-400 gap-3 px-6 text-center">
            <Image className="w-16 h-16 opacity-30" />
            <p className="text-sm">No se pudo cargar la imagen.</p>
            <p className="text-xs text-zinc-500">El archivo puede haberse movido o el almacenamiento esta temporalmente fuera de servicio.</p>
          </div>
        )}
        {imgSrc && !imgError && (
          <img
            src={imgSrc}
            alt={image.prompt}
            className={`max-w-full max-h-full object-contain rounded-lg shadow-2xl transition-[transform,opacity] duration-200 ${imgLoaded ? 'opacity-100' : 'opacity-0'}`}
            style={{ transform: `scale(${zoom})` }}
            draggable={false}
            onLoad={() => setImgLoaded(true)}
            onError={() => { setImgError(true); setImgLoaded(true); }}
          />
        )}
      </div>

      {/* Bottom Toolbar */}
      <div className="flex items-center justify-center flex-wrap gap-1.5 sm:gap-2 px-2 sm:px-4 py-2.5 sm:py-4 bg-zinc-900/80 border-t border-zinc-800">
        <div className="flex items-center gap-0.5 sm:gap-1 px-1.5 sm:px-3 py-1 sm:py-2 bg-zinc-800 rounded-lg sm:rounded-xl border border-zinc-700">
          <button onClick={() => setZoom(z => Math.max(z - 0.25, 0.5))} className="p-1.5 sm:p-2 hover:bg-zinc-700 rounded-md sm:rounded-lg transition-colors" data-testid="zoom-out-btn">
            <ZoomOut className="w-4 h-4 sm:w-5 sm:h-5 text-zinc-300" />
          </button>
          <span className="px-1.5 sm:px-3 text-[11px] sm:text-sm text-zinc-300 font-medium min-w-[40px] sm:min-w-[60px] text-center">{Math.round(zoom * 100)}%</span>
          <button onClick={() => setZoom(z => Math.min(z + 0.25, 3))} className="p-1.5 sm:p-2 hover:bg-zinc-700 rounded-md sm:rounded-lg transition-colors" data-testid="zoom-in-btn">
            <ZoomIn className="w-4 h-4 sm:w-5 sm:h-5 text-zinc-300" />
          </button>
        </div>
        <button onClick={handleCopy} className="flex items-center gap-1 sm:gap-2 px-2.5 sm:px-4 py-2 sm:py-3 bg-zinc-800 hover:bg-zinc-700 rounded-lg sm:rounded-xl border border-zinc-700 transition-colors" data-testid="fullscreen-copy-btn">
          {copied ? <Check className="w-4 h-4 sm:w-5 sm:h-5 text-green-400" /> : <Copy className="w-4 h-4 sm:w-5 sm:h-5 text-zinc-300" />}
          <span className="text-xs sm:text-sm font-medium text-zinc-200">{copied ? t.copied : t.copy}</span>
        </button>
        <button onClick={handleDownload} className="flex items-center gap-1 sm:gap-2 px-2.5 sm:px-4 py-2 sm:py-3 bg-green-600 hover:bg-green-500 rounded-lg sm:rounded-xl transition-colors" data-testid="fullscreen-download-btn">
          <Download className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
          <span className="text-xs sm:text-sm font-medium text-white">{t.download}</span>
        </button>
        <button onClick={() => { onDelete(image.id); onClose(); }} className="flex items-center gap-1 sm:gap-2 px-2.5 sm:px-4 py-2 sm:py-3 bg-red-600 hover:bg-red-500 rounded-lg sm:rounded-xl transition-colors" data-testid="fullscreen-delete-btn">
          <Trash2 className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
          <span className="text-xs sm:text-sm font-medium text-white">{t.delete}</span>
        </button>
      </div>
      
      <div className="absolute bottom-20 left-4 text-xs text-zinc-500">
        <span>ESC: {t.close} | +/-: Zoom</span>
      </div>
    </div>,
    document.body
  );
};

// 🟥 V9.0 — Thumbnail estable y robusto.
// Fixes consolidados:
// • Contenedor cuadrado fijo (aspect-square + min-h-0 + bg solid) que NO
//   colapsa antes de cargar la imagen.
// • Estado `loaded` con shimmer/pulse para evitar parpadeos.
// • Estado `errored` con icono fallback gris (no verde) cuando la imagen
//   no carga o no hay base64/url.
// • Cache-bust automático para URLs HTTP usando created_at/updated_at
//   (evita miniaturas viejas en caché del navegador).
// • Resolución defensiva del src (base64 → url → image_url → preview_url).
const Thumbnail = ({ image, fallbackIcon: FallbackIcon, apiBase }) => {
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);

  const src = (() => {
    if (image.base64 && typeof image.base64 === 'string' && image.base64.length > 16) {
      return image.base64.startsWith('data:')
        ? image.base64
        : `data:image/png;base64,${image.base64}`;
    }
    const raw = image.url || image.image_url || image.preview_url;
    if (!raw) return null;
    const full = raw.startsWith('/api') ? `${apiBase}${raw}` : raw;
    // Cache-bust solo para HTTP URLs (no afecta data: URIs).
    if (/^https?:\/\//i.test(full)) {
      const stamp = image.updated_at || image.createdAt || image.created_at;
      if (stamp) {
        const sep = full.includes('?') ? '&' : '?';
        return `${full}${sep}t=${encodeURIComponent(stamp)}`;
      }
    }
    return full;
  })();

  if (!src || errored) {
    return (
      <div
        className="w-full h-full flex items-center justify-center bg-zinc-800 text-zinc-500"
        data-testid="thumbnail-fallback"
      >
        <FallbackIcon className="w-8 h-8 opacity-40" />
      </div>
    );
  }

  return (
    <div className="relative w-full h-full bg-zinc-900 overflow-hidden">
      {!loaded && (
        <div
          className="absolute inset-0 animate-pulse bg-zinc-800"
          data-testid="thumbnail-loader"
        />
      )}
      <img
        src={src}
        alt={image.prompt || ''}
        loading="lazy"
        draggable={false}
        className="block w-full h-full object-cover transition-opacity duration-200"
        style={{ opacity: loaded ? 1 : 0 }}
        onLoad={() => setLoaded(true)}
        onError={() => { setErrored(true); setLoaded(true); }}
      />
    </div>
  );
};

/* 🟥 CUADRO NEGRO 2026-02-26 — Cache de módulo para Acciones Rápidas →
   Galería de Imágenes. Sobrevive al desmonte del modal. Reabrir es
   instantáneo (no se re-fetcha la primera página). */
const galleryImagesCache = {
  items: [],
  nextSkip: 0,
  hasMore: true,
  loadedOnce: false,
};
const PAGE_SIZE = 20;

const ImageGallery = ({ isOpen, onClose }) => {
  const { language } = useLanguage();
  const [images, setImages] = useState(galleryImagesCache.items);
  const [selectedImages, setSelectedImages] = useState(new Set());
  // 🟥 CUADRO NEGRO 2026-06-29 (rev13) — Mobile-only: id de la card
  // actualmente "activa" (tap-to-reveal). En desktop NO se usa (los botones
  // aparecen en hover). En mobile, primer tap activa la card → botones
  // aparecen. Segundo tap (cuando ya está activa) abre fullscreen.
  // Tap en otra card → activa la nueva y desactiva la anterior.
  // Click fuera del grid → desactiva todas.
  const [activeImageId, setActiveImageId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(galleryImagesCache.hasMore);
  const [fullscreenImage, setFullscreenImage] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const isFetchingRef = useRef(false);
  const scrollContainerRef = useRef(null);
  const sentinelRef = useRef(null);

  const txt = {
    es: {
      title: 'Galería de Imágenes',
      subtitle: 'Imágenes generadas guardadas',
      noImages: 'No hay imágenes guardadas',
      noImagesDesc: 'Las imágenes que guardes desde el generador aparecerán aquí',
      selectAll: 'Seleccionar todas',
      deselectAll: 'Deseleccionar todas',
      deleteSelected: 'Eliminar seleccionadas',
      confirmDelete: '¿Eliminar {count} imagen(es)?',
      deleted: 'Imagen(es) eliminada(s)',
      sentToTrash: 'Enviada(s) a la Papelera',
      delete: 'Eliminar',
      close: 'Cerrar',
      copy: 'Copiar',
      copied: 'Copiada',
      download: 'Descargar',
      imageDownloaded: 'Imagen descargada',
      imageCopied: 'Imagen copiada',
      urlCopied: 'URL copiada',
      copyError: 'Error al copiar',
      fullscreenView: 'Vista Completa',
      selected: 'seleccionada(s)',
      createdAt: 'Creada',
      loginRequired: 'Inicia sesión para ver tu galería',
      sendToWorkspace: 'Enviar al Área de Trabajo',
      sentToWorkspace: 'Enviada al área de trabajo',
      alreadyInWorkspace: 'Ya está en el área de trabajo',
      errorSending: 'Error al enviar',
      errorDeleting: 'Error al eliminar',
      errorDownloading: 'Error al descargar',
    },
    en: {
      title: 'Image Gallery',
      subtitle: 'Saved generated images',
      noImages: 'No saved images',
      noImagesDesc: 'Images you save from the generator will appear here',
      selectAll: 'Select all',
      deselectAll: 'Deselect all',
      deleteSelected: 'Delete selected',
      confirmDelete: 'Delete {count} image(s)?',
      deleted: 'Image(s) deleted',
      sentToTrash: 'Sent to Trash',
      delete: 'Delete',
      close: 'Close',
      copy: 'Copy',
      copied: 'Copied',
      download: 'Download',
      imageDownloaded: 'Image downloaded',
      imageCopied: 'Image copied',
      urlCopied: 'URL copied',
      copyError: 'Copy error',
      fullscreenView: 'Fullscreen View',
      selected: 'selected',
      createdAt: 'Created',
      loginRequired: 'Login to view your gallery',
      sendToWorkspace: 'Send to Workspace',
      sentToWorkspace: 'Sent to workspace',
      alreadyInWorkspace: 'Already in workspace',
      errorSending: 'Error sending',
      errorDeleting: 'Error deleting',
      errorDownloading: 'Error downloading',
    }
  };
  const t = txt[language] || txt.es;

  /* 🟥 fetchPage — pide una página al backend con paginación real.
   * Auth via cookie httpOnly damyxia_token (axios.defaults.withCredentials=true).
   * NO bloqueamos por getToken() porque el sentinel 'cookie-session' NO refleja
   * sesión real; el backend decide vía cookie. Si el usuario no está logueado,
   * el backend devolverá 401 y el catch lo gestiona. */
  const fetchPage = useCallback(async (skip) => {
    if (isFetchingRef.current) return null;
    isFetchingRef.current = true;
    try {
      const res = await axios.get(`${API}/gallery/images`, {
        params: { skip, limit: PAGE_SIZE, thumbnails: true },
        timeout: 15000,
      });
      const raw = res.data?.images || [];
      return raw.map(img => ({
        id: img.id,
        base64: img.base64,
        prompt: img.name || img.prompt || 'Sin nombre',
        createdAt: img.created_at,
      }));
    } catch (err) {
      console.error('[ImageGallery] page load failed:', err);
      return null;
    } finally {
      isFetchingRef.current = false;
    }
  }, []);

  // Load images from backend — only first page on open, cached on reopen.
  useEffect(() => {
    if (!isOpen) return;
    // Si ya tenemos items cacheados, mostrarlos INMEDIATAMENTE.
    if (galleryImagesCache.loadedOnce && galleryImagesCache.items.length > 0) {
      setImages(galleryImagesCache.items);
      setHasMore(galleryImagesCache.hasMore);
      setSelectedImages(new Set());
      return;
    }
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      const items = await fetchPage(0);
      if (cancelled) return;
      if (items == null) {
        // Fallback a localStorage si todo falla.
        try {
          const saved = JSON.parse(localStorage.getItem('damyxia_saved_images') || '[]');
          setImages(saved);
        } catch { setImages([]); }
        setIsLoading(false);
        setSelectedImages(new Set());
        return;
      }
      const more = items.length === PAGE_SIZE;
      galleryImagesCache.items = items;
      galleryImagesCache.nextSkip = items.length;
      galleryImagesCache.hasMore = more;
      galleryImagesCache.loadedOnce = true;
      setImages(items);
      setHasMore(more);
      setIsLoading(false);
      setSelectedImages(new Set());
    })();
    return () => { cancelled = true; };
  }, [isOpen, fetchPage]);

  /* loadMore — carga la siguiente página al hacer scroll cerca del final. */
  const loadMore = useCallback(async () => {
    if (!galleryImagesCache.hasMore || isFetchingRef.current) return;
    setIsLoadingMore(true);
    const items = await fetchPage(galleryImagesCache.nextSkip);
    if (items == null) {
      setIsLoadingMore(false);
      return;
    }
    const more = items.length === PAGE_SIZE;
    galleryImagesCache.items = [...galleryImagesCache.items, ...items];
    galleryImagesCache.nextSkip = galleryImagesCache.items.length;
    galleryImagesCache.hasMore = more;
    setImages([...galleryImagesCache.items]);
    setHasMore(more);
    setIsLoadingMore(false);
  }, [fetchPage]);

  /* IntersectionObserver para infinite scroll. */
  useEffect(() => {
    if (!isOpen) return;
    const sentinel = sentinelRef.current;
    const container = scrollContainerRef.current;
    if (!sentinel || !container) return;
    const observer = new IntersectionObserver(
      (entries) => { if (entries.some(e => e.isIntersecting)) loadMore(); },
      { root: container, rootMargin: '200px', threshold: 0 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [isOpen, loadMore, hasMore]);

  // Función legacy mantenida por compatibilidad con eliminaciones que disparen
  // un "refetch completo". Resetea el cache y vuelve a cargar la página 1.
  const loadImages = async () => {
    galleryImagesCache.items = [];
    galleryImagesCache.nextSkip = 0;
    galleryImagesCache.hasMore = true;
    galleryImagesCache.loadedOnce = false;
    setIsLoading(true);
    const items = await fetchPage(0);
    if (items != null) {
      const more = items.length === PAGE_SIZE;
      galleryImagesCache.items = items;
      galleryImagesCache.nextSkip = items.length;
      galleryImagesCache.hasMore = more;
      galleryImagesCache.loadedOnce = true;
      setImages(items);
      setHasMore(more);
    }
    setIsLoading(false);
    setSelectedImages(new Set());
  };

  const handleSelectImage = (imageId) => {
    const newSelected = new Set(selectedImages);
    if (newSelected.has(imageId)) {
      newSelected.delete(imageId);
    } else {
      newSelected.add(imageId);
    }
    setSelectedImages(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedImages.size === images.length) {
      setSelectedImages(new Set());
    } else {
      setSelectedImages(new Set(images.map(img => img.id)));
    }
  };

  // State for delete confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  const handleDeleteSelected = async () => {
    if (selectedImages.size === 0) return;
    setShowDeleteConfirm(true);
  };
  
  const confirmDeleteSelected = async () => {
    setShowDeleteConfirm(false);
    const count = selectedImages.size;
    
    setIsDeleting(true);
    try {
      const token = getToken();
      let toTrash = false;
      if (token && token !== 'guest') {
        const resp = await axios.post(`${API}/gallery/delete-multiple`, 
          { image_ids: Array.from(selectedImages) },
          {}
        );
        toTrash = Boolean(resp?.data?.to_trash);
      }
      
      const remainingImages = images.filter(img => !selectedImages.has(img.id));
      setImages(remainingImages);
      // 🟥 Sincronizar cache de módulo para no mostrar items borrados al reabrir.
      galleryImagesCache.items = remainingImages;
      galleryImagesCache.nextSkip = remainingImages.length;
      setSelectedImages(new Set());
      toast.success(toTrash ? t.sentToTrash : t.deleted);
    } catch (error) {
      console.error('Error deleting images:', error);
      toast.error(t.errorDeleting);
    }
    setIsDeleting(false);
  };

  const handleDeleteSingle = async (imageId) => {
    try {
      const token = getToken();
      let toTrash = false;
      if (token && token !== 'guest') {
        const resp = await axios.delete(`${API}/gallery/image/${imageId}`, {
        });
        toTrash = Boolean(resp?.data?.to_trash);
      }
      
      const remainingImages = images.filter(img => img.id !== imageId);
      setImages(remainingImages);
      // 🟥 Sincronizar cache de módulo.
      galleryImagesCache.items = remainingImages;
      galleryImagesCache.nextSkip = remainingImages.length;
      selectedImages.delete(imageId);
      setSelectedImages(new Set(selectedImages));
      toast.success(toTrash ? t.sentToTrash : t.deleted);
    } catch (error) {
      console.error('Error deleting image:', error);
      toast.error(t.errorDeleting);
    }
  };

  const handleDownloadImage = async (image) => {
    try {
      // Obtener el base64 sin el prefijo data:image/...
      let base64Data = image.base64 || '';
      if (base64Data.startsWith('data:')) {
        base64Data = base64Data.split(',')[1];
      }

      // Convertir base64 a bytes binarios
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);

      // Crear Blob binario real
      const blob = new Blob([byteArray], { type: 'image/png' });
      const filename = `damyxia_${image.id || Date.now()}.png`;

      // Estrategia universal: abrir en nueva pestana. El usuario usa el
      // menu del navegador → "Guardar imagen como..." para elegir carpeta.
      const result = await saveBlobWithPicker(blob, filename, 'image/png');
      if (result === 'fallback') {
        toast.success(t.imageDownloaded);
      } else {
        toast.success(
          language === 'es'
            ? 'Imagen abierta en nueva pestaña. Click derecho → "Guardar imagen como…" para elegir la carpeta.'
            : 'Image opened in a new tab. Right-click → "Save image as…" to choose the folder.',
          { duration: 7000 }
        );
      }
    } catch (error) {
      console.error('Error downloading image:', error);
      toast.error(t.errorDownloading);
    }
  };

  const handleSendToWorkspace = async (image) => {
    try {
      const token = getToken();
      if (!token || token === 'guest') {
        toast.error(t.loginRequired);
        return;
      }
      
      // Preparar el base64 con el prefijo correcto
      const base64Data = image.base64?.startsWith('data:') 
        ? image.base64 
        : `data:image/png;base64,${image.base64}`;
      
      // Primero subir la imagen para crear un image_id
      const uploadResponse = await axios.post(`${API}/workspace/upload-image`, {
        base64: base64Data,
        filename: `gallery_${image.id}.png`,
        instruction: image.prompt || ''
      }, {
      });
      
      if (uploadResponse.data.success || uploadResponse.data.image_id) {
        // Publicar la imagen al workspace usando el endpoint correcto
        const publishResponse = await axios.post(`${API}/workspace/publish`, {
          image_id: uploadResponse.data.image_id
        }, {
        });
        
        toast.success(t.sentToWorkspace);
        
        // Disparar evento con datos del workspace item para abrirlo automáticamente
        window.dispatchEvent(new CustomEvent('workspace-image-published', {
          detail: {
            workspaceItem: publishResponse.data.workspace_item,
            imageId: uploadResponse.data.image_id
          }
        }));
      }
    } catch (error) {
      if (error.response?.data?.detail?.includes('already') || error.response?.data?.already_published) {
        toast.info(t.alreadyInWorkspace);
      } else {
        console.error('Error sending to workspace:', error);
        toast.error(t.errorSending);
      }
    }
  };

  const handleExportZip = async () => {
    setIsExporting(true);
    try {
      const token = getToken();
      const res = await fetch(`${API}/export/zip`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Error al exportar');
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `DamyxIA_imagenes.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Imagenes exportadas correctamente');
    } catch (err) {
      toast.error(err.message || 'Error al exportar imagenes');
    }
    setIsExporting(false);
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return '';
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 overflow-y-auto">
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      
      <div className="relative w-full max-w-5xl bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl my-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-700 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-r from-purple-600 to-pink-600 rounded-lg">
              <Image className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-white">{t.title}</h3>
              <p className="text-xs text-zinc-400">{t.subtitle}</p>
            </div>
          </div>
          <button 
            onClick={onClose} 
            className="p-1.5 hover:bg-zinc-800 rounded-lg transition-colors"
            data-testid="close-gallery-btn"
          >
            <X className="w-5 h-5 text-zinc-400 hover:text-white" />
          </button>
        </div>

        {/* Toolbar */}
        {images.length > 0 && (
          <div className="flex items-center justify-between px-4 py-2 border-b border-zinc-800 bg-zinc-800/30 flex-shrink-0">
            <div className="flex items-center gap-3">
              <button
                onClick={handleSelectAll}
                className="flex items-center gap-2 px-3 py-1.5 text-sm text-zinc-300 hover:text-white hover:bg-zinc-700 rounded-lg transition-colors"
                data-testid="select-all-btn"
              >
                {selectedImages.size === images.length ? (
                  <CheckSquare className="w-4 h-4 text-indigo-400" />
                ) : (
                  <Square className="w-4 h-4" />
                )}
                {selectedImages.size === images.length ? t.deselectAll : t.selectAll}
              </button>
              
              {selectedImages.size > 0 && (
                <span className="text-sm text-zinc-400">
                  {selectedImages.size} {t.selected}
                </span>
              )}
              
              <button
                onClick={handleExportZip}
                disabled={isExporting || images.length === 0}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-amber-600 hover:bg-amber-500 text-white rounded-lg transition-colors disabled:opacity-50 ml-auto"
                data-testid="export-zip-btn"
                title="Exportar ZIP (Premium)"
              >
                {isExporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Archive className="w-3.5 h-3.5" />}
                ZIP
              </button>
            </div>
            
            {selectedImages.size > 0 && (
              <button
                onClick={handleDeleteSelected}
                disabled={isDeleting}
                className="flex items-center gap-2 px-3 py-1.5 text-sm bg-red-600 hover:bg-red-500 text-white rounded-lg transition-colors disabled:opacity-50"
                data-testid="delete-selected-btn"
              >
                {isDeleting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
                {t.deleteSelected} ({selectedImages.size})
              </button>
            )}
          </div>
        )}

        {/* Content */}
        <div ref={scrollContainerRef} className="flex-1 min-h-0 overflow-y-auto p-4" data-testid="image-gallery-scroll">
          {isLoading && images.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-400" />
            </div>
          ) : images.length === 0 && !isLoading ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <ImageOff className="w-16 h-16 text-zinc-600 mb-4" />
              <h4 className="text-lg font-medium text-zinc-300 mb-2">{t.noImages}</h4>
              <p className="text-sm text-zinc-500 max-w-md">{t.noImagesDesc}</p>
            </div>
          ) : (
            <>
            <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2">
              {images.map((image) => (
                <div
                  key={image.id}
                  className={`relative group rounded-lg overflow-hidden border transition-all ${
                    selectedImages.has(image.id)
                      ? 'border-indigo-500 ring-1 ring-indigo-500/30'
                      : 'border-zinc-700 hover:border-zinc-600'
                  }`}
                >
                  {/* Checkbox - más pequeño */}
                  <button
                    onClick={(e) => { e.stopPropagation(); handleSelectImage(image.id); }}
                    className="absolute top-1 left-1 z-10 p-0.5 bg-zinc-900/80 rounded transition-opacity"
                    data-testid={`select-image-${image.id}`}
                  >
                    {selectedImages.has(image.id) ? (
                      <CheckSquare className="w-3.5 h-3.5 text-indigo-400" />
                    ) : (
                      <Square className="w-3.5 h-3.5 text-zinc-400" />
                    )}
                  </button>

                  {/* Image — V9.0 Thumbnail estable con loader + error fallback.
                      🟥 Mobile tap-to-reveal: primer tap activa la card (muestra
                      los 4 botones de acción); segundo tap (ya activa) abre
                      fullscreen. Desktop ignora activeImageId y va directo al
                      fullscreen como siempre (el hover ya muestra los botones). */}
                  <div
                    className="aspect-square w-full min-h-0 cursor-pointer bg-zinc-900"
                    onClick={() => {
                      const isTouchOnly = typeof window !== 'undefined'
                        && window.matchMedia
                        && window.matchMedia('(hover: none)').matches;
                      if (isTouchOnly && activeImageId !== image.id) {
                        // Primer tap en mobile: activa la card sin abrir fullscreen.
                        setActiveImageId(image.id);
                        return;
                      }
                      // Desktop OR segundo tap en mobile (card ya activa): fullscreen.
                      setFullscreenImage(image);
                    }}
                    data-testid={`gallery-card-${image.id}`}
                  >
                    <Thumbnail image={image} fallbackIcon={Image} apiBase={API} />
                  </div>

                  {/* Overlay actions — Desktop: hover-only (opacity-0 → group-hover:opacity-100).
                      Mobile (touch): tap-to-reveal usando `activeImageId` (state local).
                      Antes el overlay quedaba `opacity-100` siempre en touch
                      devices → 4 botones tapando la imagen permanentemente.
                      Ahora arranca `opacity-0` y solo se vuelve visible
                      cuando la card está activa (tap previo) o en hover desktop. */}
                  <div
                    className={`absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-wrap items-center justify-center gap-1.5 sm:gap-1 p-1.5 sm:p-0 pointer-events-none ${
                      activeImageId === image.id ? 'opacity-100' : ''
                    }`}
                    data-active={activeImageId === image.id}
                  >
                    <button
                      onClick={(e) => { e.stopPropagation(); setFullscreenImage(image); }}
                      className="p-2 sm:p-1 bg-zinc-800 hover:bg-zinc-700 active:bg-zinc-600 rounded-lg sm:rounded transition-colors pointer-events-auto touch-manipulation"
                      title={t.fullscreenView}
                      data-testid={`open-image-${image.id}`}
                    >
                      <Maximize2 className="w-4 h-4 sm:w-3.5 sm:h-3.5 text-white" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleSendToWorkspace(image); }}
                      className="p-2 sm:p-1 bg-indigo-600 hover:bg-indigo-500 active:bg-indigo-700 rounded-lg sm:rounded transition-colors pointer-events-auto touch-manipulation"
                      title={t.sendToWorkspace}
                      data-testid={`send-to-workspace-${image.id}`}
                    >
                      <ExternalLink className="w-4 h-4 sm:w-3.5 sm:h-3.5 text-white" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDownloadImage(image); }}
                      className="p-2 sm:p-1 bg-green-600 hover:bg-green-500 active:bg-green-700 rounded-lg sm:rounded transition-colors pointer-events-auto touch-manipulation"
                      title={t.download}
                      data-testid={`download-image-${image.id}`}
                    >
                      <Download className="w-4 h-4 sm:w-3.5 sm:h-3.5 text-white" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDeleteSingle(image.id); }}
                      className="p-2 sm:p-1 bg-red-600 hover:bg-red-500 active:bg-red-700 rounded-lg sm:rounded transition-colors pointer-events-auto touch-manipulation"
                      title={t.delete}
                      data-testid={`delete-image-${image.id}`}
                    >
                      <Trash2 className="w-4 h-4 sm:w-3.5 sm:h-3.5 text-white" />
                    </button>
                  </div>

                  {/* Info - más compacto */}
                  <div className="absolute bottom-0 left-0 right-0 p-1 bg-gradient-to-t from-black/80 to-transparent">
                    <p className="text-[10px] text-zinc-300 truncate">{image.prompt}</p>
                  </div>
                </div>
              ))}
            </div>
            {/* 🟥 Sentinel para infinite scroll en la galería de Acciones Rápidas. */}
            {hasMore && (
              <div
                ref={sentinelRef}
                data-testid="image-gallery-sentinel"
                className="flex items-center justify-center py-4"
              >
                {isLoadingMore && (
                  <div className="flex items-center gap-2 text-sm text-zinc-500">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {language === 'es' ? 'Cargando más...' : 'Loading more...'}
                  </div>
                )}
              </div>
            )}
            </>
          )}
        </div>
      </div>

      {/* Fullscreen viewer */}
      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="delete-confirm-modal">
          <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-6 max-w-sm mx-4 shadow-2xl">
            <h3 className="text-lg font-semibold text-white mb-2">{language === 'es' ? 'Confirmar eliminación' : 'Confirm deletion'}</h3>
            <p className="text-zinc-400 text-sm mb-5">
              {t.confirmDelete.replace('{count}', selectedImages.size)}
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 text-sm text-zinc-300 bg-zinc-800 hover:bg-zinc-700 rounded-lg transition-colors"
                data-testid="cancel-delete-btn"
              >
                {language === 'es' ? 'Cancelar' : 'Cancel'}
              </button>
              <button
                onClick={confirmDeleteSelected}
                className="px-4 py-2 text-sm text-white bg-red-600 hover:bg-red-500 rounded-lg transition-colors"
                data-testid="confirm-delete-btn"
              >
                {language === 'es' ? 'Sí, eliminar' : 'Yes, delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {fullscreenImage && (
        <GalleryFullscreenViewer
          image={fullscreenImage}
          onClose={() => setFullscreenImage(null)}
          onDelete={handleDeleteSingle}
          t={t}
        />
      )}
    </div>,
    document.body
  );
};

// Helper function to save image to gallery (export for use in ImageGenerator)
export const saveImageToGallery = async (imageData) => {
  try {
    const token = getToken();
    
    // If logged in, save to backend
    if (token && token !== 'guest') {
      try {
        await axios.post(`${API}/gallery/save`, {
          base64: imageData.base64,
          prompt: imageData.prompt,
          size: imageData.size || '1024x1024',
          style: imageData.style || 'natural'
        }, {
        });
        return true;
      } catch (error) {
        console.error('Error saving to backend, falling back to localStorage:', error);
      }
    }
    
    // Fallback to localStorage
    const savedImages = JSON.parse(localStorage.getItem('damyxia_saved_images') || '[]');
    const newImage = {
      id: `img_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      base64: imageData.base64,
      prompt: imageData.prompt,
      createdAt: new Date().toISOString()
    };
    savedImages.unshift(newImage);
    const limitedImages = savedImages.slice(0, 50);
    localStorage.setItem('damyxia_saved_images', JSON.stringify(limitedImages));
    return true;
  } catch (error) {
    console.error('Error saving image to gallery:', error);
    return false;
  }
};

export default ImageGallery;
